import * as React from 'react';
import getScenarioStore from '../../selectors/getScenarioStore';
import SearchScenarioId from '../../store/schema/SearchScenarioId';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { SuggestionKind, Suggestion } from 'owa-search-service';
import { suggestionSelected } from '../../actions/publicActions';
import {
    LARGE_SUGGESTION_HEIGHT,
    SMALL_SUGGESTION_HEIGHT,
    SUGGESTION_ID_PREFIX,
    CLICK_ACTION_SOURCE,
} from '../../searchConstants';
import getAdditionalQuickActions from 'owa-search/lib/selectors/getAdditionalQuickActions';

const styles = require('./SearchSuggestion.scss');
const classNames = require('classnames/bind').bind(styles);

export interface SearchSuggestionProps {
    ariaLabel: string;
    content: JSX.Element;
    index: number;
    quickActions?: JSX.Element[];
    scenarioId: SearchScenarioId;
    suggestion: Suggestion;
    suggestionSetTraceId: string;
}

@observer
export default class SearchSuggestion extends React.Component<SearchSuggestionProps, {}> {
    render() {
        const { content, index, ariaLabel, scenarioId, quickActions, suggestion } = this.props;

        const classes = classNames(styles.suggestionContainer, this.isSelected && styles.selected);
        const style = {
            height: this.getHeight(),
        };

        const registerAdditionalQuickActionsFunction = getAdditionalQuickActions(scenarioId);
        let extendedQuickActions = quickActions ? quickActions.slice() : [];
        if (registerAdditionalQuickActionsFunction) {
            extendedQuickActions.unshift(...registerAdditionalQuickActionsFunction(suggestion));
        }

        return (
            <div
                aria-label={ariaLabel}
                aria-selected={this.isSelected}
                className={classes}
                id={`${SUGGESTION_ID_PREFIX}${index}`}
                role="option"
                style={style}>
                <div className={styles.contentContainer} onMouseDown={this.onMouseDown}>
                    {content}
                </div>
                {extendedQuickActions && extendedQuickActions.length > 0 && (
                    <div className={styles.quickActionsContainer}>{extendedQuickActions}</div>
                )}
            </div>
        );
    }

    /**
     * Determines whether or not this suggestion is selected.
     */
    @computed
    get isSelected(): boolean {
        const { scenarioId, index } = this.props;

        const selectedSuggestionIndex = getScenarioStore(scenarioId).selectedSuggestionIndex;
        return selectedSuggestionIndex === index;
    }

    /**
     * Determines the height of the suggestion item based on the suggestion's
     * kind.
     */
    private getHeight(): number {
        const { suggestion } = this.props;

        switch (suggestion && suggestion.kind) {
            case SuggestionKind.Keywords:
            case SuggestionKind.Category:
                return SMALL_SUGGESTION_HEIGHT;
            case SuggestionKind.People:
            case SuggestionKind.Files:
            case SuggestionKind.Message:
            default:
                return LARGE_SUGGESTION_HEIGHT;
        }
    }

    private onMouseDown = () => {
        const { scenarioId, suggestionSetTraceId, suggestion, index } = this.props;
        const { isSuggestionsCalloutClickable } = getScenarioStore(scenarioId);

        /**
         * We rely on a store value to decide if we should actually allow an
         * event to be processed. If a user double-clicks, particularly on a
         * low-end machine or a low-end browser (Edge burn), it's possible for
         * a click to happen while the SuggestionsCallout component renders
         * because it animates in slightly overlapping the input.
         */
        if (!isSuggestionsCalloutClickable) {
            return;
        }

        suggestionSelected(
            suggestion,
            CLICK_ACTION_SOURCE,
            scenarioId,
            suggestionSetTraceId,
            index
        );
    };
}
